import type { FocusEvent, ReactNode } from 'react'
import type { Content } from '@tiptap/react'
import type { RichTextEditorLabels } from '@mantine/tiptap'

export type RichTextEditorControl =
  | 'bold'
  | 'italic'
  | 'underline'
  | 'strikethrough'
  | 'clearFormatting'
  | 'highlight'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'blockquote'
  | 'hr'
  | 'bulletList'
  | 'orderedList'
  | 'link'
  | 'unlink'
  | 'undo'
  | 'redo'

type RichTextEditorPropsBase = {
  fieldName: string
  handleBlur: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  handleUpdate: (value: string) => void
  error: boolean
  content?: Content
  bottomAddOn?: ReactNode
  labels?: Partial<RichTextEditorLabels>
  /**
   * When provided, only these toolbar controls are shown and only the corresponding extensions are enabled.
   * When undefined, the full editor with all controls is shown.
   * Note: code/codeBlock are not exposed in this API — when controls is passed, pasting/typing code blocks is disabled; only full mode (controls undefined) allows them.
   */
  controls?: RichTextEditorControl[]
  /**
   * Optional test id on the editor root wrapper (not the contenteditable).
   * E2E that types into the editor may need to target the focusable/contenteditable element inside.
   */
  testId?: string
  className?: string
}

/** When editable (readOnly false or omitted), placeholder is required. When readOnly, placeholder is optional. */
export type RichTextEditorProps =
  | (RichTextEditorPropsBase & { readOnly?: false; placeholder: string })
  | (RichTextEditorPropsBase & { readOnly: true; placeholder?: string })
